import { useEffect, useState } from "react";
import { X, Send, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Shared "Submit enquiry to SparesHub" dialog.
 * Callers pre-fill `initialItems` (from a part detail, a reorder alert, or a
 * job card's parts_needed list). The dialog accepts more items inline and
 * routes to `/api/spares/enquiries` (which persists into the shared enquiries
 * collection).
 */
export default function EnquiryDialog({
  open, onClose, onSubmitted,
  title = "Submit Enquiry to SparesHub",
  initialItems = [],
  jobCardId = null,
  vehicleId = null,
  contextLabel = null,   // e.g. "For JC-2026-000012" — subtitle
  enquiry = null,        // when set, dialog operates in EDIT mode (PATCH)
}) {
  const isEdit = !!enquiry?.id;
  const [items, setItems] = useState(initialItems);
  const [urgency, setUrgency] = useState("normal");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setItems((enquiry.items || []).map((i) => ({ ...i })));
        setUrgency(enquiry.urgency || "normal");
        setNotes(enquiry.notes || "");
      } else {
        setItems(initialItems.length
          ? initialItems.map((i) => ({ ...i }))
          : [{ part_name: "", part_number: "", brand: "", spec: "", qty: 1, notes: "" }]);
        setUrgency("normal");
        setNotes("");
      }
      setError(null);
    }
     
  }, [open, enquiry?.id]);

  if (!open) return null;

  const setItem = (idx, patch) => {
    setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => setItems((r) => [...r, { part_name: "", part_number: "", brand: "", spec: "", qty: 1, notes: "" }]);
  const removeRow = (idx) => setItems((r) => r.filter((_, i) => i !== idx));

  const validRows = items.filter((r) => (r.part_name || "").trim());
  const canSubmit = validRows.length > 0 && !saving;

  const submit = async () => {
    if (!canSubmit) { setError("Add at least one part with a name"); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        urgency,
        items: validRows.map((r) => ({
          part_name: r.part_name,
          part_number: r.part_number || "",
          brand: r.brand || "",
          spec: r.spec || "",
          qty: Number(r.qty) || 1,
          notes: r.notes || "",
        })),
        notes,
      };
      if (isEdit) {
        await api.patch(`/spares/enquiries/${enquiry.id}`, payload);
        toast.success(`Enquiry ${enquiry.enquiry_number} updated`);
      } else {
        await api.post("/spares/enquiries", {
          ...payload,
          job_card_id: jobCardId,
          vehicle_id: vehicleId,
        });
        toast.success(`Enquiry submitted for ${validRows.length} part${validRows.length === 1 ? "" : "s"}`);
      }
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not submit enquiry");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="enquiry-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
           data-testid="enquiry-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#1d3557] text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-white/15 grid place-items-center">
              <Send size={16} />
            </div>
            <div>
              <h2 className="text-[15px] font-bold">{title}</h2>
              <p className="text-[11.5px] text-white/70">
                {contextLabel || "Send parts your vendors don't stock to the SparesHub network."}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-white/10 grid place-items-center"
                  data-testid="enquiry-dialog-close">
            <X size={17} />
          </button>
        </header>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085]">Parts to enquire</div>
              <button type="button" onClick={addRow}
                      data-testid="enquiry-add-row"
                      className="h-7 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                <Plus size={11} /> Add another
              </button>
            </div>
            {items.map((it, idx) => (
              <div key={idx} className="rounded-lg border border-[#E4E7EC] p-3 space-y-2" data-testid={`enquiry-row-${idx}`}>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  <input value={it.part_name} onChange={(e) => setItem(idx, { part_name: e.target.value })}
                         placeholder="Part name *"
                         data-testid={`enquiry-row-${idx}-name`}
                         className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
                  <input value={it.part_number} onChange={(e) => setItem(idx, { part_number: e.target.value })}
                         placeholder="Part number"
                         className="h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
                  <input value={it.brand} onChange={(e) => setItem(idx, { brand: e.target.value })}
                         placeholder="Brand"
                         className="h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
                  <div className="flex gap-2">
                    <input type="number" min={1} value={it.qty}
                           onChange={(e) => setItem(idx, { qty: Number(e.target.value) || 1 })}
                           placeholder="Qty"
                           data-testid={`enquiry-row-${idx}-qty`}
                           className="flex-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeRow(idx)}
                              data-testid={`enquiry-row-${idx}-remove`}
                              className="w-9 h-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#a8dadc] hover:text-[#e63946] hover:border-[#e63946]/40">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
                <input value={it.spec || ""} onChange={(e) => setItem(idx, { spec: e.target.value })}
                       placeholder="Specification / variant (optional)"
                       className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Urgency</span>
              <select value={urgency} onChange={(e) => setUrgency(e.target.value)}
                      data-testid="enquiry-urgency"
                      className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <div className="rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[11.5px] text-[#1d3557]">
              <div className="font-bold">Where does this go?</div>
              <div className="text-[#457b9d]">SparesHub back-office picks it up and returns quotes with availability.</div>
            </div>
          </div>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Notes to SparesHub (optional)</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="enquiry-notes"
                      placeholder="Delivery deadline, condition (new/OEM/refurb), preferred brands…"
                      className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="enquiry-error">
              {error}
            </div>
          )}
        </div>

        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={submit} disabled={!canSubmit}
                  data-testid="enquiry-submit-btn"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Send size={13} />
            {saving
              ? (isEdit ? "Saving…" : "Submitting…")
              : (isEdit ? "Save changes" : "Submit enquiry")}
          </button>
        </footer>
      </div>
    </div>
  );
}
